import type { FormEvent } from "react";
import { Alerta, Button, Desplegable, MontoField, Row, Select, Stack, TextField } from "@/components";
import type { Moneda, TipoCuota } from "@/lib/api";
import { formatMoneda } from "@/lib/format";
import { filaCuota, OPCIONES_TIPO_CUOTA, sumaFilas, type ErroresFilas, type ErroresPlan, type FilaCuota, type FormPlan } from "@/lib/ventas";

function ErrorBloque({ texto }: { texto: string | undefined }) {
  return texto ? <p className="text-chico text-peligro">{texto}</p> : null;
}

export interface FormularioPlanProps {
  /** Precio de la venta (decimal del backend), en la moneda de cuenta. */
  precio: string;
  monedaCuenta: Moneda;
  form: FormPlan;
  cambiar: <K extends keyof FormPlan>(campo: K, valor: FormPlan[K]) => void;
  erroresPlan: ErroresPlan;
  filas: FilaCuota[];
  setFilas: (filas: FilaCuota[]) => void;
  erroresFilas: ErroresFilas;
  /** Pidiendo la vista previa al backend (el botón Generar queda cargando). */
  generando: boolean;
  errorVistaPrevia: string | null;
  generar: () => void;
  onSubmit: (e: FormEvent) => void;
  onCancelar: () => void;
  guardando: boolean;
  error: string | null;
}

/**
 * Plan de cuotas: el generador pide la vista previa al backend (acá no se calcula nada) y la vuelca en la tabla
 * de filas, que el admin puede retocar antes de guardar. Se guarda siempre la tabla.
 */
export function FormularioPlan({
  precio, monedaCuenta, form, cambiar, erroresPlan, filas, setFilas, erroresFilas, generando, errorVistaPrevia, generar, onSubmit, onCancelar, guardando, error,
}: FormularioPlanProps) {
  const suma = sumaFilas(filas);
  const cierra = suma === precio;
  const cambiarFila = <K extends keyof FilaCuota>(i: number, campo: K, valor: FilaCuota[K]) => setFilas(filas.map((f, j) => (j === i ? { ...f, [campo]: valor } : f)));
  const cambiarRefuerzo = (i: number, campo: "monto" | "fecha", valor: string) =>
    cambiar("refuerzos", form.refuerzos.map((r, j) => (j === i ? { ...r, [campo]: valor } : r)));

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack espacio={4}>
        <Desplegable titulo="Generador" abiertoInicial>
          <Stack espacio={4}>
            <Stack espacio={2}>
              <MontoField etiqueta="Anticipo" valor={form.anticipo_monto} onCambio={(x) => cambiar("anticipo_monto", x)} moneda={monedaCuenta} />
              <TextField etiqueta="Fecha del anticipo" type="date" value={form.anticipo_fecha} onChange={(e) => cambiar("anticipo_fecha", e.target.value)} />
              <ErrorBloque texto={erroresPlan.anticipo} />
            </Stack>
            <Stack espacio={2}>
              <TextField etiqueta="Cantidad de cuotas" inputMode="numeric" value={form.cuotas_cantidad} onChange={(e) => cambiar("cuotas_cantidad", e.target.value.trim())} />
              <MontoField etiqueta="Monto por cuota" valor={form.cuotas_monto} onCambio={(x) => cambiar("cuotas_monto", x)} moneda={monedaCuenta} />
              <TextField etiqueta="Primer vencimiento" type="date" value={form.primer_vencimiento} onChange={(e) => cambiar("primer_vencimiento", e.target.value)} />
              <ErrorBloque texto={erroresPlan.cuotas} />
            </Stack>
            <Stack espacio={2}>
              {form.refuerzos.map((r, i) => (
                <Stack key={i} espacio={2} className="rounded-control border border-borde p-3">
                  <MontoField etiqueta={`Refuerzo ${i + 1}`} valor={r.monto} onCambio={(x) => cambiarRefuerzo(i, "monto", x)} moneda={monedaCuenta} />
                  <TextField etiqueta={`Fecha del refuerzo ${i + 1}`} type="date" value={r.fecha} onChange={(e) => cambiarRefuerzo(i, "fecha", e.target.value)} />
                  <Row justificar="fin">
                    <Button variante="fantasma" tamanio="sm" aria-label={`Quitar el refuerzo ${i + 1}`} onClick={() => cambiar("refuerzos", form.refuerzos.filter((_, j) => j !== i))}>
                      Quitar
                    </Button>
                  </Row>
                </Stack>
              ))}
              <div>
                <Button variante="secundario" tamanio="sm" onClick={() => cambiar("refuerzos", [...form.refuerzos, { monto: "", fecha: "" }])}>
                  Agregar refuerzo
                </Button>
              </div>
              <ErrorBloque texto={erroresPlan.refuerzos} />
            </Stack>
            <Stack espacio={2}>
              <MontoField etiqueta="Posesión" valor={form.posesion_monto} onCambio={(x) => cambiar("posesion_monto", x)} moneda={monedaCuenta} />
              <TextField etiqueta="Fecha de posesión" type="date" value={form.posesion_fecha} onChange={(e) => cambiar("posesion_fecha", e.target.value)} />
              <ErrorBloque texto={erroresPlan.posesion} />
            </Stack>
            <Alerta texto={erroresPlan.general} />
            <Alerta texto={errorVistaPrevia} />
            <div>
              <Button variante="secundario" cargando={generando} onClick={generar}>
                Generar
              </Button>
            </div>
          </Stack>
        </Desplegable>

        <Stack espacio={3}>
          <h3 className="font-medium">Cuotas</h3>
          {filas.length === 0 && <p className="text-chico text-texto-suave">Generá el plan o agregá las cuotas a mano.</p>}
          {filas.map((f, i) => (
            <Stack key={i} espacio={2} className="rounded-control border border-borde p-3">
              <Select etiqueta={`Tipo de la cuota ${i + 1}`} opciones={OPCIONES_TIPO_CUOTA} valor={f.tipo} onCambio={(x) => cambiarFila(i, "tipo", x as TipoCuota)} />
              <TextField etiqueta={`Vencimiento de la cuota ${i + 1}`} type="date" value={f.vencimiento} onChange={(e) => cambiarFila(i, "vencimiento", e.target.value)} />
              <MontoField etiqueta={`Monto de la cuota ${i + 1}`} valor={f.monto_base} onCambio={(x) => cambiarFila(i, "monto_base", x)} moneda={monedaCuenta} />
              <Row justificar="fin">
                <Button variante="fantasma" tamanio="sm" aria-label={`Quitar la cuota ${i + 1}`} onClick={() => setFilas(filas.filter((_, j) => j !== i))}>
                  Quitar
                </Button>
              </Row>
            </Stack>
          ))}
          <div>
            <Button variante="secundario" tamanio="sm" onClick={() => setFilas([...filas, filaCuota()])}>
              Agregar cuota
            </Button>
          </div>
          <p className={cierra ? "text-chico text-texto-suave" : "text-chico text-peligro"}>{`Suma ${formatMoneda(suma ?? "0", monedaCuenta)} de ${formatMoneda(precio, monedaCuenta)}`}</p>
          <Alerta texto={erroresFilas.filas} />
        </Stack>

        <Alerta texto={error} />
        <Row justificar="fin" espacio={2}>
          <Button variante="secundario" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button type="submit" cargando={guardando}>
            Guardar plan
          </Button>
        </Row>
      </Stack>
    </form>
  );
}
